import { useState } from "react";

import { FormRow, Field, FormHelpers } from "../forms";
import { useFormInput } from "../../hooks/use-form-input";
import {
  EditHourlyRatePayload,
  isValidRequiredHourlyRate,
  useEditHourlyRateValidator,
  useModifyPosition
} from "../../hooks/use-modify-position";
import { useAccessControl } from "../../app/use-access-control";
import { StaffMember } from "../../hooks/use-user";
import { HourlyRateHistoryRow } from "../../hooks/use-employment-history";
import { useModal } from "../modal/modal";
import { LoadingOverlay } from "../loading-overlay/loading-overlay";

export interface UseEditHourlyRateHistoryModalProps {
  staffMember: StaffMember | undefined;
}

export const useEditHourlyRateHistoryModal = ({ staffMember }: UseEditHourlyRateHistoryModalProps) => {
  const { activeCenterId } = useAccessControl();
  const [initialEndDate, setInitialEndDate] = useState<string>();
  const { show, hide: hideEditHourlyRateHistoryModal, renderModal } = useModal();

  const { buffer, onChangeValue, setBuffer } = useFormInput<EditHourlyRatePayload>({
    id: 0,
    hourlyRate: "",
    effectiveDate: "",
    end: ""
  });

  const isValidEditHourlyRatePayload = useEditHourlyRateValidator(buffer);
  const { editHourlyRateMutator } = useModifyPosition(
    activeCenterId,
    staffMember?.id ?? "",
    hideEditHourlyRateHistoryModal
  );

  const onSave = () => {
    editHourlyRateMutator.mutate(buffer);
  };

  const showEditHourlyRateHistoryModal = (rate: HourlyRateHistoryRow) => {
    setBuffer({
      id: rate.id,
      hourlyRate: String(rate.hourlyRate),
      effectiveDate: rate.start,
      end: rate.end
    });
    setInitialEndDate(rate.end);
    show();
  };

  const renderEditHourlyRateHistoryModal = () =>
    renderModal({
      title: `Hourly Rate History - ${staffMember?.displayName}`,
      body: (
        <LoadingOverlay loading={editHourlyRateMutator.isLoading}>
          <FormRow>
            <Field
              type="text"
              label="Hourly rate"
              required
              mode="edit"
              value={buffer.hourlyRate}
              onChangeValue={onChangeValue("hourlyRate")}
              onValidate={isValidRequiredHourlyRate}
            />
          </FormRow>

          <FormRow>
            <Field
              type="date"
              label="Start Date"
              required
              mode="edit"
              value={buffer.effectiveDate}
              onChangeValue={onChangeValue("effectiveDate")}
              onValidate={FormHelpers.isValidRequiredDateString}
            />
          </FormRow>

          <FormRow>
            <Field
              type="date"
              label="End Date"
              required
              mode={initialEndDate ? "edit" : "view"}
              value={buffer.end ?? ""}
              onChangeValue={onChangeValue("end")}
              onValidate={FormHelpers.isValidDateString}
            />
          </FormRow>
        </LoadingOverlay>
      ),
      footer: (
        <>
          <button className="btn btn-secondary" onClick={hideEditHourlyRateHistoryModal}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={!isValidEditHourlyRatePayload}>
            Save
          </button>
        </>
      )
    });

  return {
    hideEditHourlyRateHistoryModal,
    showEditHourlyRateHistoryModal,
    renderEditHourlyRateHistoryModal
  };
};
